import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import CircularProgress from '@mui/material/CircularProgress';
import { Stack } from '@mui/system';

import CreatePaymentDialog from '@/shared/components/dialogs/CreatePaymentDialog';
import WaveUIProgressDialog from '@/shared/components/dialogs/WaveUIProgressDialog';
import CustomerDetailsPanel from '@/shared/components/panels/CustomerDetailsPanel';
import PaymentsDetailsPanel from '@/shared/components/panels/PaymentsDetailsPanel';
import WaveUINotificationBar from '@/shared/components/utilities/WaveUINotificationBar';
import AgentNotes from '@/shared/components/widgets/AgentNotes/AgentNotes';
import useAgentNotes from '@/shared/components/widgets/AgentNotes/hooks/useAgentNotes';
import { AppPath } from '@/shared/constants/path';
import useCreatePayment from '@/shared/hooks/useCreatePayment';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import DialogName from '@/shared/types/dialogName.enum';
import Queue from '@/shared/types/fulfilment/queue.type';

import CancelOrderDialog from './components/CancelOrderDialog';
import FulfilmentOrdersTable_v2 from './components/FulfilmentOrdersTable_v2/FulfilmentOrdersTable_v2';
import FulfilmentQueueTable from './components/FulfilmentQueueTable/FulfilmentQueueTable';
import OrderDetails from './components/OrderDetails';
import Title from './components/Title';
import useOrderEditOperations from './hooks/useOrderEditOperations';

const OrderEdit: FC = () => {
  const { id } = useParams();
  const orderId = id || '';

  const { dialog, onUpdateDialogState } = useModalDialog<unknown, DialogName>();
  const { notificationBar, onUpdateNotificationBar } = useNotificationBar();
  const { progressDialog, onUpdateProgressDialog } = useProgressDialog();

  const { order, customer, setRefetchOrder, handleCancelOrder } =
    useOrderEditOperations({
      orderId,
      onUpdateDialogState,
      onUpdateNotificationBar,
    });

  const [fulfilmentQueueTableRows, setFulfilmentQueueTableRows] = useState<
    Queue[]
  >([]);

  const {
    paymentStatuses,
    paymentMethods,
    paymentFormData,
    paymentFormErrors,
    setPaymentFormData,
    handleCreatePayment,
    onValidatePaymentFormField,
    onValidatePaymentForm,
  } = useCreatePayment({
    resetState: dialog?.createPayment?.open ?? false,
    onUpdateDialogState,
    onUpdateNotificationBar,
    setRefetchOrder,
  });

  const agentNotesState = useAgentNotes({
    customerId: customer?.in_customer_id || '',
    module: 'orders',
    moduleItemId: order?.in_order_id || '',
  });

  if (!order || !customer) {
    return (
      <Stack
        alignItems='center'
        justifyContent='center'
        height='100%'
        width='100%'
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <>
      <Title
        orderId={order.in_order_id}
        orderStatus={order.order_status}
        queuesLength={fulfilmentQueueTableRows.length}
        submitToFulfilment={() => {
          onUpdateDialogState(DialogName.SubmitAllQueues, {
            open: true,
            errors: [],
          });
        }}
        redirectToInvoicePage={() => {
          window
            .open(`${AppPath.Invoices}${AppPath.Orders}/${orderId}`, '_blank')
            ?.focus();
        }}
        cancelOrder={() => {
          onUpdateDialogState(DialogName.CancelOrder, {
            open: true,
            errors: [],
          });
        }}
      />

      <Stack
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto minmax(500px, 1fr) 1fr',
          alignItems: 'start',
          gap: 2,
          mb: 10,
        }}
      >
        <Stack spacing={2}>
          <CustomerDetailsPanel
            customer={customer}
            isBillingAddressVisible
            isShippingAddressVisible
          />

          <PaymentsDetailsPanel
            title='Payments Details'
            payments={order.payments}
            mode='create'
            onCreatePayment={() =>
              onUpdateDialogState(DialogName.CreatePayment, {
                open: true,
                errors: [],
              })
            }
          />
        </Stack>

        <OrderDetails order={order} />

        <AgentNotes title='Notes' agentNotesState={agentNotesState} />
      </Stack>

      <Stack spacing={2}>
        <FulfilmentQueueTable
          order={order}
          onUpdateNotificationBar={onUpdateNotificationBar}
          getTableStateRows={setFulfilmentQueueTableRows}
        />

        <FulfilmentOrdersTable_v2 orderId={orderId} />
      </Stack>

      {dialog?.createPayment?.open && (
        <CreatePaymentDialog
          orderId={orderId}
          paymentStatuses={paymentStatuses}
          paymentMethods={paymentMethods}
          paymentFormData={paymentFormData}
          paymentFormErrors={paymentFormErrors}
          setPaymentFormData={setPaymentFormData}
          onValidatePaymentFormField={onValidatePaymentFormField}
          onValidatePaymentForm={onValidatePaymentForm}
          errors={dialog.createPayment.errors}
          loading={dialog.createPayment.loading}
          onClose={() => {
            onUpdateDialogState(DialogName.CreatePayment, { open: false });
          }}
          proceed={(values) => {
            void handleCreatePayment(values);
          }}
        />
      )}

      {dialog?.cancelOrder?.open && (
        <CancelOrderDialog
          order={order}
          errors={dialog?.cancelOrder.errors}
          loading={dialog?.cancelOrder.loading}
          onClose={() => {
            onUpdateDialogState(DialogName.CancelOrder, { open: false });
          }}
          proceed={(isCloseWithRefund) => {
            void handleCancelOrder(orderId, isCloseWithRefund);
          }}
        />
      )}

      <WaveUINotificationBar
        message={notificationBar.message}
        severity={notificationBar.severity}
        open={notificationBar.open}
        close={() => onUpdateNotificationBar({ open: false })}
      />

      <WaveUIProgressDialog
        title={progressDialog?.title}
        progress={progressDialog?.progress || 0}
        open={progressDialog?.open || false}
        onClose={() => onUpdateProgressDialog({ open: false })}
      />
    </>
  );
};

export default OrderEdit;
